import {Card, CardContent, Grid, IconButton, Stack, Typography, TextField, Button} from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import React, {useEffect, useState, useContext} from "react";
import useCallDataApi from "../../hooks/data";
import {useSnackbar} from "notistack";
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, convertToRaw, convertFromRaw, ContentState } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import {useAuth} from "../../context/AuthContext";
import HighlightPanel from "../../components/HighlightPanel";
import DetailPanel from "../../components/DetailPanel";
import {useConfig} from "../../context/ConfigContext";

const TourOfficeDetail = ({tourOfficeId}) => {
    const [tourOffice, setTourOffice] = useState({})
    const [contactPersons, setContactPersons] = useState([])
    const [comments, setComments] = useState([])
    const [editorState, setEditorState] = useState(EditorState.createEmpty())
    const [editingComment, setEditingComment] = useState(null)
    const {getData: fetchTourOffice} = useCallDataApi('tour-offices')
    const {postData: postContactPerson} = useCallDataApi('contact-person')
    const {getData: fetchComments, createData: createComment, updateData: updateComment, deleteData: deleteComment} = useCallDataApi('tour-office-comments')
    const {enqueueSnackbar} = useSnackbar()
    const {getUser} = useAuth()
    const user = getUser()
    const permissions = user?.permissions

    useEffect(() => {
        fetchTourOffice(tourOfficeId).then(t => setTourOffice(t))
        postContactPerson('get_for_tour_office', {tour_office_id: tourOfficeId}).then(c => setContactPersons(c))
        fetchComments(`by_tour_office?tour_office_id=${tourOfficeId}`).then(c => setComments(c))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const sendMail = (contact) => {
        if (contact?.email) window.open(`mailto:${contact?.email}`)
    }

    const callNumber = (contact) => {
        if (contact?.phone) window.open(`tel:${contact?.phone}`)
    }

    const handleAddComment = () => {
        const contentState = editorState.getCurrentContent();
        const rawContentState = convertToRaw(contentState);
        const htmlContent = draftToHtml(rawContentState);

        if (htmlContent.trim()) {
            createComment({
                tour_office: tourOfficeId,
                comment: htmlContent
            }).then(comment => {
                setComments([comment, ...comments])
                setEditorState(EditorState.createEmpty())
                enqueueSnackbar('Megjegyzés hozzáadva!', {variant: 'success'})
            })
        }
    }

    const handleEditComment = (comment) => {
        try {
            const contentBlock = htmlToDraft(comment.comment);
            if (contentBlock && contentBlock.contentBlocks) {
                const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks, contentBlock.entityMap);
                const editorState = EditorState.createWithContent(contentState);
                setEditorState(editorState);
                setEditingComment(comment);
            }
        } catch (error) {
            console.error('Error editing comment:', error);
            enqueueSnackbar('Hiba történt a megjegyzés szerkesztése közben', {variant: 'error'});
        }
    }

    const handleUpdateComment = () => {
        const contentState = editorState.getCurrentContent();
        const rawContentState = convertToRaw(contentState);
        const htmlContent = draftToHtml(rawContentState);

        if (htmlContent.trim() && editingComment) {
            updateComment(editingComment.id, {
                tour_office: tourOfficeId,
                comment: htmlContent
            }).then(updatedComment => {
                setComments(comments.map(c => c.id === updatedComment.id ? updatedComment : c));
                setEditorState(EditorState.createEmpty());
                setEditingComment(null);
                enqueueSnackbar('Megjegyzés módosítva!', {variant: 'success'});
            });
        }
    }

    const handleDeleteComment = (commentId) => {
        if (window.confirm('Biztosan törölni szeretnéd ezt a megjegyzést?')) {
            deleteComment(commentId).then(() => {
                setComments(comments.filter(c => c.id !== commentId));
                enqueueSnackbar('Megjegyzés törölve!', {variant: 'success'});
            });
        }
    }

    const renderComment = (comment) => {
        try {
            const contentBlock = htmlToDraft(comment.comment);
            if (contentBlock) {
                const contentState = EditorState.createWithContent(contentBlock);
                const rawContentState = convertToRaw(contentState.getCurrentContent());
                return draftToHtml(rawContentState);
            }
            return comment.comment;
        } catch (error) {
            console.error('Error rendering comment:', error);
            return comment.comment;
        }
    }

    const {generalColor} = useConfig()

    return <>
        <HighlightPanel color={generalColor}>
            <Typography gutterBottom variant="h5" component="div">
                Általános infó
            </Typography>
            <DetailPanel details={[{
                name: 'Név',
                value: tourOffice?.name
            }, {
                name: 'Rövid név',
                value: tourOffice?.short_name,
            }, {
                name: 'Cím',
                value: tourOffice?.address,
            }, {
                name: 'Adószám',
                value: tourOffice?.vat_number
            }, {
                name: 'Cég/Magánszemély',
                value: tourOffice?.is_company ? 'Cég' : 'Magánszemély'
            }]}/>
        </HighlightPanel>

        {permissions?.includes('add_tourofficecomment') && (
            <Card sx={{minWidth: 350, marginTop: 2}}>
                <CardContent>
                    <Typography gutterBottom variant="h5" component="div">
                        {editingComment ? 'Megjegyzés szerkesztése' : 'Új megjegyzés'}
                    </Typography>
                    <Stack spacing={2}>
                        <div style={{ border: '1px solid #ccc', padding: '10px', minHeight: '200px' }}>
                            <Editor
                                editorState={editorState}
                                onEditorStateChange={setEditorState}
                                toolbar={{
                                    options: ['inline', 'blockType', 'fontSize', 'fontFamily', 'list', 'textAlign', 'colorPicker', 'link', 'emoji', 'history', 'image'],
                                }}
                            />
                        </div>
                        <Stack direction="row" spacing={2}>
                            <Button 
                                variant="contained" 
                                color="success" 
                                onClick={editingComment ? handleUpdateComment : handleAddComment}
                                disabled={!editorState.getCurrentContent().hasText()}
                            >
                                {editingComment ? 'Módosítás' : 'Hozzáadás'}
                            </Button>
                            {editingComment && (
                                <Button 
                                    variant="outlined" 
                                    color="error" 
                                    onClick={() => {
                                        setEditorState(EditorState.createEmpty());
                                        setEditingComment(null);
                                    }}
                                >
                                    Mégse
                                </Button>
                            )}
                        </Stack>
                    </Stack>
                </CardContent>
            </Card>
        )}

        {permissions?.includes('view_tourofficecomment') && comments.length > 0 && <Typography variant="h5" gutterBottom marginTop={2}>Megjegyzések:</Typography>}
        <Grid container spacing={2}>
            {permissions?.includes('view_tourofficecomment') && comments.map(comment => (
                <Grid key={comment.id} item xs={12}>
                    <Card>
                        <CardContent>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                <div style={{ flex: 1 }} dangerouslySetInnerHTML={{ __html: renderComment(comment) }} />
                                {(permissions?.includes('change_tourofficecomment') || permissions?.includes('delete_tourofficecomment')) && (
                                    <Stack direction="row" spacing={1}>
                                        {permissions?.includes('change_tourofficecomment') && (
                                            <IconButton 
                                                size="small" 
                                                color="primary" 
                                                onClick={() => handleEditComment(comment)}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        )}
                                        {permissions?.includes('delete_tourofficecomment') && (
                                            <IconButton 
                                                size="small" 
                                                color="error" 
                                                onClick={() => handleDeleteComment(comment.id)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        )}
                                    </Stack>
                                )}
                            </Stack>
                            <Typography variant="caption" color="text.secondary">
                                {new Date(comment.created_at).toLocaleString()}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            ))}
        </Grid>

        {contactPersons.length > 0 && <Typography variant="h5" gutterBottom marginTop={2}>Kapcsolattartók:</Typography>}
        <Grid container spacing={2}>
            {contactPersons.map(contact => <Grid key={contact.id} item xs={7} lg={6}>
                <Card sx={{maxWidth: 345}}>
                    <CardContent>
                        <Typography gutterBottom variant="h6" component="div">
                            {contact?.first_name} {contact?.last_name}
                        </Typography>
                        <Stack spacing={1}>
                            <Stack direction='row' spacing={2} alignItems='center'>
                                <IconButton color='success' onClick={() => callNumber(contact)}><PhoneIcon/></IconButton>
                                <Typography variant="body2" color="text.secondary">{contact?.phone ? contact?.phone : '-'}</Typography>
                            </Stack>
                            <Stack direction='row' spacing={2} alignItems='center'>
                                <IconButton color='success' onClick={() => sendMail(contact)}><EmailIcon/></IconButton>
                                <Typography variant="body2" color="text.secondary">{contact?.email ? contact?.email : '-'}</Typography>
                            </Stack>
                        </Stack>
                    </CardContent>
                </Card>
            </Grid>)}
        </Grid>
    </>
}

export default TourOfficeDetail